// src/services/SupabaseBackend.js
// Supabase implementation of the StorageAdapter backend interface.
//
// Stores all key-value pairs in the rms_config table:
//   key   TEXT PRIMARY KEY
//   value JSONB
//
// Sync methods (getItem, setItem, removeItem, keys) are provided for interface
// compatibility but have limited functionality with Supabase:
//   - getItem() returns null  — reads must go through hydrate() + cache
//   - setItem() fires a background upsert — cache keeps data consistent
//   - removeItem() fires a background delete
//   - keys() returns [] — use keysAsync() for a complete list
//
// Async methods (getItemAsync, setItemAsync, removeItemAsync, keysAsync) are
// fully implemented against Supabase and are the primary data access path.

import { createClient } from '@supabase/supabase-js';

class SupabaseBackend {
  constructor(supabaseUrl, supabaseKey) {
    this.client = createClient(supabaseUrl, supabaseKey);
    this.table = 'rms_config';
  }

  // ── Sync interface (backward compat) ──────────────────────────────────────

  // Supabase reads are async — sync read is not available.
  // All reads should go through hydrate() + cache.
  getItem(_key) {
    return null;
  }

  // Fire-and-forget async upsert.
  // The StorageAdapter cache is updated synchronously by the caller (setJSON),
  // so this session's reads remain consistent without waiting for the network.
  setItem(key, value) {
    this._upsertAsync(key, value).catch(err =>
      console.error('[SupabaseBackend] setItem failed for key:', key, err)
    );
  }

  // Fire-and-forget async delete.
  removeItem(key) {
    this._deleteAsync(key).catch(err =>
      console.error('[SupabaseBackend] removeItem failed for key:', key, err)
    );
  }

  // Sync keys not available — use keysAsync() instead.
  keys() {
    return [];
  }

  // ── Async interface (used by StorageAdapter.hydrate) ──────────────────────

  async getItemAsync(key) {
    const { data, error } = await this.client
      .from(this.table)
      .select('value')
      .eq('key', key)
      .maybeSingle();

    if (error) {
      console.error('[SupabaseBackend] getItemAsync error for key:', key, error);
      return null;
    }
    if (!data) return null;

    // JSONB is returned as a parsed JS value — stringify for StorageAdapter
    return JSON.stringify(data.value);
  }

  async setItemAsync(key, value) {
    await this._upsertAsync(key, value);
  }

  async removeItemAsync(key) {
    await this._deleteAsync(key);
  }

  async keysAsync() {
    const { data, error } = await this.client
      .from(this.table)
      .select('key');

    if (error) {
      console.error('[SupabaseBackend] keysAsync error:', error);
      return [];
    }
    return data ? data.map(row => row.key) : [];
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  async _upsertAsync(key, value) {
    // value is a raw JSON string from StorageAdapter — parse for JSONB column
    let parsed;
    try {
      parsed = JSON.parse(value);
    } catch (_e) {
      parsed = value; // store raw string if not valid JSON
    }

    const { error } = await this.client
      .from(this.table)
      .upsert({ key, value: parsed }, { onConflict: 'key' });

    if (error) throw error;
  }

  async _deleteAsync(key) {
    const { error } = await this.client
      .from(this.table)
      .delete()
      .eq('key', key);

    if (error) throw error;
  }
}

export default SupabaseBackend;
